import { instantiate, fillSlot, clearSlot } from '@classic/Shared/TemplateLoader';
import type { PopupAlert } from '@classic/Components/PopUpAlerts/Schemas';
import popUpAlertTemplate from '@classic/Components/PopUpAlerts/Components/PopUpAlert.html?raw';
import alertIconSvg from '@classic/Components/PopUpAlerts/Assets/AlertIcon.svg?raw';

/**
 * Light-DOM custom element rendering a single alert's content, replicating
 * `Legacy\PopUpAlert\ModalContent::getHTML()`. All structural markup (headings,
 * paragraphs, the CTA button/icon) lives statically in `PopUpAlert.html` —
 * named `<slot>` placeholders act as light-DOM query hooks, not Shadow DOM
 * slot projection. Render logic here only ever mutates that static structure
 * (text, attributes, `ss-hidden` toggling); it never builds wrapper elements
 * with `createElement`. All alert data is written via `textContent`/DOM APIs
 * (never `innerHTML`) so the API response can never inject markup — the XSS
 * surface the legacy `htmlentities()` calls covered server-side now lives
 * here, enforced structurally instead of by escaping.
 */
class PopUpAlert extends HTMLElement {
    private alertData: PopupAlert | null = null;

    public set alert(value: PopupAlert) {
        this.alertData = value;
        this.render();
    }

    public get alert(): PopupAlert | null {
        return this.alertData;
    }

    private render(): void {
        if (!this.alertData) {
            return;
        }
        const alert = this.alertData;

        const fragment = instantiate(popUpAlertTemplate);
        fillSlot(fragment, 'icon', instantiate(alertIconSvg));
        this.renderTitle(fragment, alert.title);
        fillSlot(fragment, 'message', this.buildMessageNodes(alert.message));
        this.renderCta(fragment, alert);
        fillSlot(fragment, 'timeSince', alert.timeSince);

        this.replaceChildren(fragment);
    }

    private renderTitle(root: DocumentFragment, title: string): void {
        root.querySelector('.ss-alert-modal-title')?.classList.toggle('ss-hidden', !title);
        fillSlot(root, 'title', title);
    }

    /** `<br>` count is data-dependent, so the message body is the one part that must be built, not just slotted. */
    private buildMessageNodes(message: string): Node[] {
        const nodes: Node[] = [];
        message.split('\n').forEach((line, index) => {
            if (index > 0) {
                nodes.push(document.createElement('br'));
            }
            nodes.push(document.createTextNode(line));
        });
        return nodes;
    }

    private renderCta(root: DocumentFragment, alert: PopupAlert): void {
        const container = root.querySelector<HTMLDivElement>('.ss-alert-modal-button-container');
        const link = root.querySelector<HTMLAnchorElement>('.ss-alert-modal-cta-button');
        const linkText = alert.linkText;
        const normalizedLink = alert.link ? this.normalizeLink(alert.link) : '';
        const isValid = Boolean(normalizedLink && linkText && this.isValidLink(normalizedLink));

        container?.classList.toggle('ss-hidden', !isValid);
        if (!isValid || !link || !linkText) {
            clearSlot(root, 'ctaText');
            return;
        }

        link.href = normalizedLink;
        link.target = this.isSameOriginLink(normalizedLink) ? '_self' : '_blank';
        if (link.target === '_blank') {
            link.rel = 'noopener';
        }
        fillSlot(root, 'ctaText', linkText);
    }

    /**
     * Prepend https:// to bare domains so existing DB rows and draft preview
     * still pass `isValidLink`.
     *
     * Intentionally duplicates `SmartSites.normalizeAdminUrl` /
     * `SmartSites\Util\Url::addProtocol` — this public component may run on
     * pages that do not load the admin `SmartSites` global. Keep the
     * passthrough scheme list in sync with those helpers when either changes.
     */
    private normalizeLink(link: string): string {
        const trimmed = link.trim();
        if (trimmed === '') {
            return link;
        }
        const lower = trimmed.toLowerCase();
        const passthroughPrefixes = [
            'https://',
            'http://',
            'ftps://',
            'ftp://',
            'mailto:',
            'tel:',
            '//',
            '/',
            '#',
        ];
        if (passthroughPrefixes.some((prefix) => lower.startsWith(prefix))) {
            return trimmed;
        }
        return `https://${trimmed}`;
    }

    /** Only http(s) and relative paths are ever rendered as a real `href`. */
    private isValidLink(link: string): boolean {
        if (link.startsWith('/')) {
            return true;
        }
        try {
            return ['http:', 'https:'].includes(new URL(link).protocol);
        } catch {
            return false;
        }
    }

    /** @see src/SmartSites/Util/Url.php::link_target() */
    private isSameOriginLink(link: string): boolean {
        if (link.startsWith('/')) {
            return true;
        }
        try {
            return new URL(link).host === window.location.host;
        } catch {
            return false;
        }
    }
}

if (!customElements.get('ss-popup-alert')) {
    customElements.define('ss-popup-alert', PopUpAlert);
}

export default PopUpAlert;
